import { CircleHelp, Copy } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";


import { toast } from "sonner";
import { LabelText, SmallText, XsmallText } from "../../lib/ui/components/typography/Typography";
import Button from "../../lib/ui/components/button/Button";
import Tooltip from "../../lib/ui/components/tooltip/Tooltip";
import Checkbox from "../../lib/ui/components/checkbox/Checkbox";
import Input from "../../lib/ui/components/input/Input";
import Textarea from "../../lib/ui/components/input/Textarea";
import { ensureInterviewTypesArray, isVoiceFeatureEnabled, isInterviewTypePickerEnabled } from "../../utils/interviewTypes";
import Toggle from "../../lib/ui/components/toggle/Toggle";
import JobDocumentsUpload from "./JobDocumentsUpload";
import CollapsibleSection from "../../lib/ui/components/collapsible-section/CollapsibleSection";
import SectionFooter from "../../lib/ui/components/section-footer/SectionFooter";
import VisibilityHint from "./VisibilityHint";
import "./RoleDetailsForm.css";


const ADDITIONAL_COMPANY_INFO_QUESTIONS = [
    "Why the role is open",
    "Next steps after the interview",
    "Compensation, including bonus or commission",
    "Who the role reports to",
    "When you expect to hire",
];

const RoleDetailsForm = ({
    isNewJobMode,
    roleName,
    setRoleName,
    companyName,
    setCompanyName,
    briefDescription,
    setBriefDescription,
    displayDescription,
    setDisplayDescription,
    companyLocation,
    setCompanyLocation,
    companyValues,
    setCompanyValues,
    companyBenefits,
    setCompanyBenefits,
    companyAdditionalInfo,
    setCompanyAdditionalInfo,
    interviewProcess,
    setInterviewProcess,
    roleDocuments,
    setRoleDocuments,
    rolePurpose,
    setRolePurpose,
    isCreatingJob,
    onCreateJob,
    onUpdateRole,
    jobCode,
    automaticInterviews,
    setAutomaticInterviews,
    companyId,
    jobId,
    token,
    roleContextCharacterCount,
    roleContextCharacterLimit,
    hasAnalysisRubric = false,
    onAnalysisRubricRequired,
    allowedInterviewTypes: propAllowedInterviewTypes,
    setAllowedInterviewTypes: propSetAllowedInterviewTypes,
    companyDefaultInterviewTypes = ["TEXT"],
    fullWidth = false,
    footerHost,
    savedRevision = 0,
    onAdvance,
    onDirtyStateChange,
    detailsRef,
}) => {
    const [localAllowedInterviewTypes, setLocalAllowedInterviewTypes] = useState(
        propAllowedInterviewTypes || companyDefaultInterviewTypes || ["TEXT"]
    );
    const [isContinuing, setIsContinuing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const allowedInterviewTypes = propAllowedInterviewTypes !== undefined ? propAllowedInterviewTypes : localAllowedInterviewTypes;
    const setAllowedInterviewTypes = propSetAllowedInterviewTypes || setLocalAllowedInterviewTypes;

    useEffect(() => {
        if (isNewJobMode) {
            if (propAllowedInterviewTypes !== undefined) {
                const propTypes = ensureInterviewTypesArray(propAllowedInterviewTypes);
                setLocalAllowedInterviewTypes(prev => {
                    const prevTypes = ensureInterviewTypesArray(prev);
                    if (JSON.stringify([...propTypes].sort()) !== JSON.stringify([...prevTypes].sort())) {
                        return propTypes;
                    }
                    return prev;
                });
            } else if (companyDefaultInterviewTypes) {
                const defaults = ensureInterviewTypesArray(companyDefaultInterviewTypes);
                setLocalAllowedInterviewTypes(prev => {
                    const prevTypes = ensureInterviewTypesArray(prev);
                    if (JSON.stringify([...defaults].sort()) !== JSON.stringify([...prevTypes].sort())) {
                        return defaults;
                    }
                    return prev;
                });
            }
        }
    }, [isNewJobMode, companyDefaultInterviewTypes, propAllowedInterviewTypes]);

    const handleInterviewTypeChange = (type, checked) => {
        const currentTypes = ensureInterviewTypesArray(allowedInterviewTypes, []);
        let newTypes;
        if (checked) {
            newTypes = [...currentTypes, type];
        } else {
            newTypes = currentTypes.filter(t => t !== type);
        }
        setAllowedInterviewTypes(newTypes);
    };

    // New-job mode only: enabling requires an analysis rubric, which a new
    // role never has yet, so turning it on routes to the warning modal.
    const handleAutomaticInterviewsToggle = (checked) => {
        if (checked && !hasAnalysisRubric) {
            onAnalysisRubricRequired?.();
            return;
        }
        setAutomaticInterviews(checked);
    };

    const handleCopyJobCode = () => {
        if (jobCode) {
            navigator.clipboard.writeText(jobCode).then(() => {
                toast.success("Job ID copied to clipboard!");
            }).catch(() => {
                toast.error("Failed to copy Job ID");
            });
        } else {
            toast.info("No Job ID to copy");
        }
    };

    const roleSnapshot = () => ({
        roleName: roleName ?? "",
        companyName: companyName ?? "",
        briefDescription: briefDescription ?? "",
        displayDescription: displayDescription ?? "",
        companyLocation: companyLocation ?? "",
        companyValues: companyValues ?? "",
        companyBenefits: companyBenefits ?? "",
        companyAdditionalInfo: companyAdditionalInfo ?? "",
        interviewProcess: interviewProcess ?? "",
    });
    const roleSnapshotRef = useRef(roleSnapshot);
    roleSnapshotRef.current = roleSnapshot;
    const [savedRole, setSavedRole] = useState(roleSnapshot);
    useEffect(() => {
        setSavedRole(roleSnapshotRef.current());
    }, [savedRevision]);

    const currentRole = roleSnapshot();
    const isRoleDirty = Object.keys(currentRole).some(
        (key) => currentRole[key] !== savedRole[key]
    );

    const revertRole = useCallback(() => {
        setRoleName(savedRole.roleName);
        setCompanyName(savedRole.companyName);
        setBriefDescription(savedRole.briefDescription);
        setDisplayDescription(savedRole.displayDescription);
        setCompanyLocation(savedRole.companyLocation);
        setCompanyValues(savedRole.companyValues);
        setCompanyBenefits(savedRole.companyBenefits);
        setCompanyAdditionalInfo(savedRole.companyAdditionalInfo);
        setInterviewProcess(savedRole.interviewProcess);
    }, [
        savedRole,
        setRoleName,
        setCompanyName,
        setBriefDescription,
        setDisplayDescription,
        setCompanyLocation,
        setCompanyValues,
        setCompanyBenefits,
        setCompanyAdditionalInfo,
        setInterviewProcess,
    ]);

    useEffect(() => {
        if (isNewJobMode) return undefined;
        onDirtyStateChange?.(isRoleDirty);
        return () => onDirtyStateChange?.(false);
    }, [isNewJobMode, isRoleDirty, onDirtyStateChange]);

    useEffect(() => {
        if (!detailsRef) return undefined;
        detailsRef.current = { discard: revertRole };
        return () => {
            detailsRef.current = null;
        };
    }, [detailsRef, revertRole]);

    const canLeaveRoleDetails = Boolean(
        roleName?.trim() && companyName?.trim() && (briefDescription ?? "").trim()
    );
    const isButtonDisabled = isNewJobMode
        ? (!roleName || !companyName || !briefDescription || !rolePurpose || isCreatingJob || ensureInterviewTypesArray(allowedInterviewTypes, []).length === 0)
        : (!canLeaveRoleDetails || isContinuing || isSaving);

    const saveRoleDetailsOnly = async () => {
        if (!isRoleDirty || isButtonDisabled) return;
        setIsSaving(true);
        try {
            await onUpdateRole?.();
            toast.success("Role details saved");
        } catch (error) {
            toast.error(error?.message || "Could not save role details");
        } finally {
            setIsSaving(false);
        }
    };

    const continueFromRoleDetails = async () => {
        if (isButtonDisabled) return;
        if (isRoleDirty) {
            setIsContinuing(true);
            try {
                await onUpdateRole?.();
                toast.success("Role details saved");
            } catch (error) {
                toast.error(error?.message || "Could not save role details");
                return;
            } finally {
                setIsContinuing(false);
            }
        }
        onAdvance?.();
    };

    return (
        <div className={`${fullWidth ? "w-full" : "w-[40%] p-4"} bg-white role-details`}>
            <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-sky-50 border border-sky-200 rounded-md">
                <VisibilityHint label="June can share this information with applicants" size={16} />
                <XsmallText emphasis={true} weight="semibold">
                    June can share this information with applicants
                </XsmallText>
            </div>

            <CollapsibleSection
                title="Role information"
                contentId="role-information-content"
                dataCy="role-information-section"
            >
                <div className="mb-2">
                    <div className="flex items-center">
                        {isNewJobMode ? (
                            <>
                                <XsmallText emphasis={true} weight="semibold">Role Purpose <span className="text-red-500">*</span></XsmallText>
                                <Tooltip
                                    position="right"
                                    content="This selection will define the type of welcome message the candidate receives."
                                >
                                    <CircleHelp size="1em" className="text-gray-500 cursor-pointer ml-2" />
                                </Tooltip>
                            </>
                        ) : (
                            <div className="flex items-center gap-2">
                                <XsmallText emphasis={true} weight="semibold">Role Purpose:</XsmallText>
                                <XsmallText emphasis={false}>{
                                    rolePurpose === "new_applicants" && "New Applicants"
                                    || rolePurpose === "candidate_rediscovery" && "Candidate Rediscovery"
                                    || rolePurpose === "candidate_outreach" && "Candidate Outreach"
                                }</XsmallText>
                            </div>
                        )}
                    </div>

                {isNewJobMode && (
                    <div className="flex justify-between w-full">
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name="rolePurpose"
                                value="new_applicants"
                                checked={rolePurpose === "new_applicants"}
                                onChange={() => setRolePurpose("new_applicants")}
                                className="form-radio h-4 w-4"
                                style={{ color: 'var(--primary-700)' }}
                                disabled={isCreatingJob}
                            />
                            <XsmallText>New Applicants</XsmallText>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name="rolePurpose"
                                value="candidate_rediscovery"
                                checked={rolePurpose === "candidate_rediscovery"}
                                onChange={() => setRolePurpose("candidate_rediscovery")}
                                className="form-radio h-4 w-4"
                                style={{ color: 'var(--primary-700)' }}
                                disabled={isCreatingJob}
                            />
                            <XsmallText>Candidate Rediscovery</XsmallText>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name="rolePurpose"
                                value="candidate_outreach"
                                checked={rolePurpose === "candidate_outreach"}
                                onChange={() => setRolePurpose("candidate_outreach")}
                                className="form-radio h-4 w-4"
                                style={{ color: 'var(--primary-700)' }}
                                disabled={isCreatingJob}
                            />
                            <XsmallText>Candidate Outreach</XsmallText>
                        </label>
                    </div>
                )}
            </div>

            {isNewJobMode && (
                <div className="mt-[24px]">
                    <div className="flex items-start gap-3">
                        <Toggle
                            checked={automaticInterviews}
                            disabled={isCreatingJob}
                            onChange={handleAutomaticInterviewsToggle}
                        />
                        <div className="min-w-0 flex-1">
                            <XsmallText emphasis={true} weight="semibold">Automatic Interviews</XsmallText>
                            <XsmallText>
                                Automatically send interviews to eligible candidates when interview questions are available.
                            </XsmallText>
                        </div>
                    </div>
                </div>
            )}

            <div className="mt-[24px]">
                <div className="flex items-center items-center justify-between mb-[10px]">
                    <XsmallText emphasis={true} weight="semibold">Role Name <span className="text-red-500">*</span></XsmallText>
                </div>
                <Input
                    id="role-name-input"
                    value={roleName}
                    onChange={(e) => setRoleName(e.target.value)}
                    background="white"
                    className="h-10"
                    disabled={isCreatingJob}
                    placeholder="Enter the Role Name e.g. 'IT manager'"
                />
            </div>

            {/* Job ID Field */}
            <div className="mt-[24px]">
                <div className="flex items-center items-center justify-between mb-[10px]">
                    <XsmallText emphasis={true} weight="semibold">Job ID</XsmallText>
                </div>
                <div className="relative">
                    <Input
                        id="job-code-input"
                        value={jobCode || "--"}
                        readOnly
                        background="white"
                        className="h-10 pr-10 cursor-default"
                        placeholder="--"
                    />
                    <button
                        type="button"
                        onClick={handleCopyJobCode}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-black hover:text-gray-700 transition-colors"
                        title="Copy Job ID"
                    >
                        <Copy size={10} />
                    </button>
                </div>
            </div>

            <div className="mt-[24px]">
                <div className="flex items-center items-center justify-between mb-[10px]">
                    <XsmallText emphasis={true} weight="semibold">Company Name <span className="text-red-500">*</span></XsmallText>
                </div>
                <Input
                    id="company-name-input"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    background="white"
                    className="h-10"
                    disabled={isCreatingJob}
                    placeholder="Enter the Company Name e.g. 'Tech Solutions Ltd.'"
                />
            </div>

            <div className="mt-[24px]">
                <div className="flex items-center justify-between mb-[10px]">
                    <XsmallText emphasis={true} weight="semibold">Job Description <span className="text-red-500">*</span></XsmallText>
                    {/* TODO: Add generate description functionality */}
                    {/* <Button variant="tertiary" leftIcon={<WandSparkles />}>Generate</Button> */}
                </div>
                <Textarea
                    id="role-description-input"
                    background="white"
                    value={isNewJobMode ? briefDescription : displayDescription}
                    onChange={(e) => {
                        if (isNewJobMode) {
                            setBriefDescription(e.target.value);
                        } else {
                            setDisplayDescription(e.target.value);
                            setBriefDescription(e.target.value);
                        }
                    }}
                    className="h-[150px]"
                    disabled={isCreatingJob}
                    placeholder="Paste your job description or give us a few details about the role and what characteristics you're looking for."
                />
            </div>
            </CollapsibleSection>

            <CollapsibleSection
                title="Additional Company Context"
                contentId="additional-company-context-content"
                dataCy="additional-information-section"
                className="mt-4"
            >
                <XsmallText className="mb-4 block">
                    The interviewer shares this when candidates ask about the company, the role or what happens next.
                </XsmallText>
                        <div>
                            <div className="flex items-center justify-between mb-[10px]">
                                <XsmallText emphasis={true} weight="semibold">Company Culture</XsmallText>
                            </div>
                            <Textarea
                                value={companyValues}
                                onChange={(e) => setCompanyValues(e.target.value)}
                                background="white"
                                className="h-[80px]"
                                disabled={isCreatingJob}
                                maxLength={5000}
                                placeholder="Describe your company culture and values"
                            />
                        </div>
                        <div className="mt-[16px]">
                            <div className="flex items-center justify-between mb-[10px]">
                                <XsmallText emphasis={true} weight="semibold">Interview Process</XsmallText>
                            </div>
                            <Textarea
                                value={interviewProcess}
                                onChange={(e) => setInterviewProcess(e.target.value)}
                                background="white"
                                className="h-[80px]"
                                disabled={isCreatingJob}
                                maxLength={5000}
                                placeholder="Describe the interview process and what candidates should expect at each step"
                            />
                        </div>

                            <div className="mt-[16px]">
                                <div className="flex items-center justify-between mb-[10px]">
                                    <XsmallText emphasis={true} weight="semibold">Company Location</XsmallText>
                                </div>
                                <Input
                                    value={companyLocation}
                                    onChange={(e) => setCompanyLocation(e.target.value)}
                                    background="white"
                                    className="h-10"
                                    disabled={isCreatingJob}
                                    placeholder="Enter company location"
                                />
                            </div>
                            <div className="mt-[16px]">
                                <div className="flex items-center justify-between mb-[10px]">
                                    <XsmallText emphasis={true} weight="semibold">Company Benefits</XsmallText>
                                </div>
                                <Textarea
                                    value={companyBenefits}
                                    onChange={(e) => setCompanyBenefits(e.target.value)}
                                    background="white"
                                    className="h-[80px]"
                                    disabled={isCreatingJob}
                                    maxLength={5000}
                                    placeholder="List the benefits and perks your company offers"
                                />
                            </div>
                            <div className="mt-[16px]">
                                <div className="flex items-center gap-2 mb-[10px]">
                                    <XsmallText emphasis={true} weight="semibold">Additional Company Information</XsmallText>
                                    <Tooltip
                                        position="top"
                                        content={
                                            <span className="block text-left">
                                                {"Candidates commonly ask about:\n"}
                                                {ADDITIONAL_COMPANY_INFO_QUESTIONS.map(
                                                    (question) => `• ${question}`
                                                ).join("\n")}
                                            </span>
                                        }
                                    >
                                        <CircleHelp size="1em" className="text-gray-500 cursor-pointer" />
                                    </Tooltip>
                                </div>
                                <Textarea
                                    value={companyAdditionalInfo}
                                    onChange={(e) => setCompanyAdditionalInfo(e.target.value)}
                                    background="white"
                                    className="h-[80px]"
                                    disabled={isCreatingJob}
                                    maxLength={5000}
                                    placeholder="Any other relevant company information to share with candidates"
                                />
                            </div>
                            <div className="mt-[16px]">
                                <div className="mb-[10px]">
                                    <XsmallText emphasis={true} weight="semibold">Candidate-facing documents</XsmallText>
                                    <XsmallText>
                                        Anything a candidate may be shown or told about: job spec, benefits summary, team overview.
                                    </XsmallText>
                                </div>
                                <JobDocumentsUpload
                                    documents={roleDocuments}
                                    onChange={setRoleDocuments}
                                    disabled={isCreatingJob}
                                    dataCy="role-documents-input"
                                    audience="candidate_visible"
                                    companyId={companyId}
                                    jobId={jobId}
                                    token={token}
                                    contextCount={roleContextCharacterCount}
                                    contextLimit={roleContextCharacterLimit}
                                />
                            </div>
            </CollapsibleSection>

            {isNewJobMode && isInterviewTypePickerEnabled() && (
                <div className="mt-[24px]">
                    <div className="flex items-center justify-between mb-[10px]">
                        <XsmallText emphasis={true} weight="semibold">
                            Allowed Interview Types
                        </XsmallText>
                    </div>
                    <div className="flex flex-col gap-3">
                        <label className="flex items-center gap-3 cursor-pointer">
                            <Checkbox
                                checked={ensureInterviewTypesArray(allowedInterviewTypes, []).includes("TEXT")}
                                onChange={(e) => handleInterviewTypeChange("TEXT", e.target.checked)}
                                disabled={isCreatingJob}
                            />
                            <div className="flex-1">
                                <span className="font-medium text-gray-900">Text (SMS)</span>
                                <div className="text-small text-gray-500">
                                    Candidates respond via text messages
                                </div>
                            </div>
                        </label>
                        {isVoiceFeatureEnabled() && (
                        <label className="flex items-center gap-3 cursor-pointer">
                            <Checkbox
                                checked={ensureInterviewTypesArray(allowedInterviewTypes, []).includes("VOICE")}
                                onChange={(e) => handleInterviewTypeChange("VOICE", e.target.checked)}
                                disabled={isCreatingJob}
                            />
                            <div className="flex-1">
                                <span className="font-medium text-gray-900">Voice</span>
                                <div className="text-small text-gray-500">
                                    Candidates respond via voice calls
                                </div>
                            </div>
                        </label>
                        )}
                    </div>
                    {ensureInterviewTypesArray(allowedInterviewTypes, []).length === 0 && (
                        <p className="text-small text-red-500 mt-2">
                            Please select at least one interview type
                        </p>
                    )}
                </div>
            )}

            <SectionFooter host={footerHost}>
                {!isNewJobMode && (
                    <>
                        <Button
                            variant="secondary"
                            onClick={revertRole}
                            disabled={!isRoleDirty || isContinuing || isSaving}
                            data-cy="reset-role-details-button"
                        >
                            Reset
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={saveRoleDetailsOnly}
                            disabled={!isRoleDirty || isButtonDisabled}
                            data-cy="save-role-details-button"
                        >
                            {isSaving ? "Saving..." : "Save"}
                        </Button>
                    </>
                )}
                <Button
                    id="submit-create-role-button"
                    variant="primary"
                    disabled={isButtonDisabled}
                    onClick={isNewJobMode ? onCreateJob : continueFromRoleDetails}
                    data-cy={!isNewJobMode ? "update-role-button" : undefined}
                >
                    {isNewJobMode
                        ? (isCreatingJob ? "Creating..." : "Create Role")
                        : (isContinuing ? "Saving..." : isRoleDirty ? "Save & Continue" : "Continue")}
                </Button>
            </SectionFooter>
        </div>
    );
};

export default RoleDetailsForm;
